/**
 * Plan 08 RecipeDetailPanel — the recipe detail surface that opens
 * when a card in the /recipes grid is clicked.
 *
 * Mirrors the /orders kitchen detail panel exactly: a plain `Card`
 * (NOT an overlay Sheet) that the page positions in a right-hand
 * column. The grid content shifts left to make room, and a
 * "Full screen" affordance promotes the same panel into a centered
 * Dialog. `variant` toggles the two presentations:
 *   - 'panel'      → bordered/rounded card in the side slot
 *   - 'fullscreen' → borderless, fills the Dialog
 *
 * Surfaces (top → bottom):
 *   - Title (Component.name) in the sticky header + Close
 *   - Kind / WGR / subgroup badges + auto-description
 *   - Allergen chips (full list — not capped like on the card)
 *   - Dietary chips (vegetarian / halal / gluten-free / etc.)
 *   - Ingredients list (default-variant recipe)
 *   - Recipe meta (prep / cook time / yield)
 *
 * Deferred to follow-on plans (documented next to each surface):
 *   - "On the menu this week" CTA + deep-link → needs Plan 11/12
 *   - 🇺🇸 USA badge per ingredient → needs `Ingredient.madeInUsa`
 *   - Storage / serving temp chips → needs Tag.kind = STORAGE_TEMP
 *   - Per-serving Nutrition table → needs the nutrition resolver
 *   - Authorized alternates (swap-for list) → Plan 43 surface
 */

import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { AllergenTagChip, DietaryTagChip } from '@ordo/ui/tag-chip'
import { Maximize2, X } from 'lucide-react'

import {
  pickDefaultRecipeVariant,
  RECIPE_KIND_LABELS,
  type RecipeDetailResponse,
  useRecipeDetailQuery,
} from '@/api/recipes'
import { AssetImage } from '@/components/asset-image'
import { Button } from '@/components/chefs/button'
import { CHIP_CLASS_SM, ChipButton } from '@/components/chefs/chip-button'
import { RecipeDetailSections } from '@/components/chefs/recipe-detail-sections'
import { RecipeKindMonogram } from '@/components/recipes/recipe-kind-monogram'
import { toChefRecipe } from '@/lib/recipes/chef-recipe-adapter'

interface RecipeDetailPanelProps {
  componentVariantId?: string | null
  componentId: string
  onClose: () => void
  onFullscreen?: (componentId: string) => void
  variant?: 'panel' | 'fullscreen'
}

export function RecipeDetailPanel({
  componentId,
  componentVariantId,
  onClose,
  onFullscreen,
  variant = 'panel',
}: RecipeDetailPanelProps) {
  const { data, isPending, error } = useRecipeDetailQuery(componentId)
  const isFullscreen = variant === 'fullscreen'
  const component = data?.component ?? null

  return (
    <Card
      className={cn(
        'flex h-full w-full flex-col overflow-hidden bg-background p-0',
        isFullscreen ? 'border-0 shadow-none' : 'shrink-0 border-0 border-border lg:rounded-2xl lg:border lg:shadow-xs',
      )}>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-background px-6 pb-3 pt-5">
          <h2 className="pr-2 text-xl font-medium leading-tight">
            {component ? component.name : isPending ? <Skeleton className="h-6 w-44" /> : 'Recipe'}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            className="-mr-2 -mt-1 text-muted-foreground"
            onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {!isFullscreen ? (
          <div className="flex flex-wrap items-center gap-2 px-6 pb-5">
            <ChipButton
              Icon={Maximize2}
              className={cn(CHIP_CLASS_SM, 'hidden lg:inline-flex')}
              onClick={() => onFullscreen?.(componentId)}>
              Full screen
            </ChipButton>
          </div>
        ) : null}

        {isPending && !data ? (
          <div className="px-6 pb-6">
            <PanelLoading />
          </div>
        ) : null}
        {error ? (
          <div className="px-6 pb-6">
            <PanelError message={error.message} />
          </div>
        ) : null}
        {component ? <PanelBody component={component} componentVariantId={componentVariantId} /> : null}
      </div>
    </Card>
  )
}

function PanelLoading() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Loading recipe">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  )
}

function PanelError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      Couldn&apos;t load this recipe. {message}
    </div>
  )
}

function AttrRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between py-1 text-sm">
      <span className="font-medium text-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  )
}

function StackedAttr({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 py-1.5 text-sm">
      <span className="font-medium text-foreground">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  )
}

function PanelBody({
  component,
  componentVariantId,
}: {
  component: NonNullable<RecipeDetailResponse['component']>
  componentVariantId?: string | null
}) {
  const recipeVariant = componentVariantId
    ? component.variants.find((candidate) => candidate.id === componentVariantId)
    : pickDefaultRecipeVariant(component.variants)
  // `recipeVariant` is always defined for a Component that ever shipped
  // on a Menu (per Plan 08's "Default-Variant Invariant"), but the
  // catalog can briefly host components mid-edit without a variant.
  // Guard the recipe lookup to avoid a runtime throw on the rare row.
  const recipe = recipeVariant?.recipe ? toChefRecipe(recipeVariant.recipe) : undefined

  const allergenTags = component.tags.filter((t) => t.tag.kind === 'ALLERGEN')
  const dietaryTags = component.tags.filter((t) => t.tag.kind === 'DIETARY')

  return (
    <>
      <div className="flex flex-col gap-5 px-6 pb-6 lg:flex-row">
        {component.imageUri ? (
          <AssetImage
            src={component.imageUri}
            className="h-[240px] w-full shrink-0 rounded-xl object-cover lg:w-[240px]"
            fallback={
              <RecipeKindMonogram
                kind={component.kind}
                className="h-[240px] w-full shrink-0 rounded-xl lg:w-[240px]"
                iconClassName="h-16 w-16"
              />
            }
          />
        ) : (
          <RecipeKindMonogram
            kind={component.kind}
            className="h-[240px] w-full shrink-0 rounded-xl lg:w-[240px]"
            iconClassName="h-16 w-16"
          />
        )}

        <div className="flex flex-1 flex-col">
          <AttrRow label="Type" value={RECIPE_KIND_LABELS[component.kind]} />
          {component.vegetableSubgroup ? (
            <AttrRow label="Subgroup" value={readableEnum(component.vegetableSubgroup)} />
          ) : null}
          {component.grainExhibitGroup ? (
            <AttrRow label="Grain group" value={readableEnum(component.grainExhibitGroup)} />
          ) : null}
          <AttrRow label="Whole grain" value={component.wholeGrainRich ? 'Yes' : 'No'} />

          <Separator className="my-2" />

          {component.autoDescription ? (
            <p className="py-1 text-sm text-muted-foreground">{component.autoDescription}</p>
          ) : null}

          {dietaryTags.length > 0 ? (
            <StackedAttr label="Dietary">
              {dietaryTags.map((t) => (
                <DietaryTagChip key={t.id} label={t.tag.name} className="rounded-full px-2 py-0.5 text-[11px]" />
              ))}
            </StackedAttr>
          ) : null}

          {allergenTags.length > 0 ? (
            <StackedAttr label="Allergens">
              {allergenTags.map((t) => (
                <AllergenTagChip key={t.id} className="rounded-full px-2 py-0.5 text-[11px]">
                  {t.tag.name}
                </AllergenTagChip>
              ))}
            </StackedAttr>
          ) : null}
        </div>
      </div>

      <div className="border-t border-border px-5 py-5">
        <RecipeDetailSections
          recipe={recipe}
          emptyMessage="No recipe attached yet — the kitchen catalog team is still drafting this one."
        />
      </div>
    </>
  )
}

/** "DARK_GREEN" → "Dark green". Used to humanize enum chips. */
function readableEnum(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
}
